/**
 * PATH: packages/ui/src/kanban/components/KanbanCardQuickCapture.tsx
 * FILE_ID: UI-KANBAN-CARD-QUICKCAPTURE-001
 * PURPOSE: Card-face quick-capture input — hover reveals a slim "note / date…"
 *   field; JUST TYPE (no click) while the card is hovered, ↵ to save. A bare
 *   date sets the card's due date; "action <date>" sets the ACTION date;
 *   anything else is appended as a progress note on the comment thread, without
 *   opening the detail drawer.
 * WHY: Operator 2026-06-24: "I should be able to hover over the card and start
 *   typing the date or something or notes and it just gets added to it."
 *   FEAT-2026-07-02-KANBAN-CARD-TYPE-TO-CAPTURE, operator: "Hovering over the
 *   card I can't type things like action date and such.. or notes directly" —
 *   so hover now routes bare printable keys straight into this field (see
 *   shouldClaimTypeToCapture), and the classifier grew an action-date form.
 *   Hovered-card hotkeys (1/2/3 priority, p pin, c archive) are NOT claimed —
 *   they must keep working on hover. Digit-leading dates ("22/7"): click the
 *   field first, or use a letter form ("due 22/7", "tomorrow").
 * FLOW:
 *   ┌────────┐  ↵   ┌──────────────────────┐  due  ┌──────────────────┐
 *   │ typed  │ ───▶ │ classifyQuickCapture  │ ────▶ │ onSetDueDate(iso)│
 *   │ text   │      │ (pure: date vs note)  │  note ┌──────────────────┐
 *   └────────┘      └──────────────────────┘ ────▶ │ api.createComment │
 *                                                   └──────────────────┘
 * DEPENDENCIES:
 *   - ./quickCapture: the pure classifier (date → due / else → note).
 *   - ./peekQueryKeys cardCommentsQueryKey: invalidate so the thread + count
 *     repaint after a note lands (same key the peek/drawer read).
 *   - ./card-detail/commentDraft: shared local draft with drawer/peek Card notes
 *     (same `kanban:comment-draft:<user>:<card>` key — typing continues across
 *     surfaces). Hover `visible=false` must NEVER clear the draft.
 *   - @tanstack/react-query useQueryClient: cache invalidation.
 * RELATED:
 *   - ./KanbanCardActionZone.tsx: renders this as its `quickCaptureSlot`.
 *   - ./KanbanCard.tsx: wires it when the host supplies `api` + `quickActions`.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CalendarClock, Check, Loader2, StickyNote } from 'lucide-react'

import type { KanbanApi } from '../api'
import type { Card as KanbanCardModel } from '../types'
import {
  foldUnclaimedUppercaseKey,
  KANBAN_SHORTCUTS,
  loadShortcutOverrides,
  resolveCanonicalKey,
} from '../shortcuts/registry'
import {
  commentDraftScope,
  migrateAnonCommentDraft,
  readCommentDraft,
  useCommentDraft,
} from './card-detail/commentDraft'
import { cardCommentsQueryKey } from './peekQueryKeys'
import { classifyQuickCapture } from './quickCapture'

export interface KanbanCardQuickCaptureProps {
  card: KanbanCardModel
  api: Pick<KanbanApi, 'createComment' | 'updateCard'>
  /** Card hover state — drives tab-stop gating (the field isn't a tab target
   *  while the zone is collapsed) and the placeholder verbosity. Does NOT
   *  clear the local draft when it flips false. */
  visible: boolean
  /** Current viewer id — scopes the LOCAL draft (shared with drawer/peek Card
   *  notes). Absent → device-scoped 'anon'. */
  currentUserId?: string | number | null
  /** Board-wired optimistic due setter (preferred over a raw api.updateCard so
   *  the tile's due pill repaints instantly + the audit row matches the rail). */
  onSetDueDate?: (card: KanbanCardModel, dueDate: string | null) => Promise<unknown> | void
  /** FEAT-2026-07-02-KANBAN-CARD-TYPE-TO-CAPTURE: board-wired action-date
   *  setter for the "action 22 jul" capture form. Omit -> that form falls
   *  back to a raw api.updateCard patch. */
  onSetActionDate?: (card: KanbanCardModel, actionDate: string | null) => Promise<unknown> | void
  /** Signal the host to refetch this card (so the footer comment count / due
   *  pill reflect the new note/date). Reuses the board's card-changed pipe. */
  onChanged?: (card: KanbanCardModel) => void
}

type Flash = { tone: 'ok'; text: string } | { tone: 'err'; text: string } | null

/** Stop the card's drag / open / keyboard-shortcut handlers from firing while
 *  the operator interacts with the field. */
function swallow(e: { stopPropagation: () => void }) {
  e.stopPropagation()
}

/** f(document keydown) -> should type-to-capture claim this key? True only for
 *  a bare printable character pressed while nothing editable owns focus, so
 *  hovering a card and just typing starts a capture without stealing browser /
 *  app shortcuts or hijacking an input the operator is already in.
 *
 *  Hovered-card hotkeys (priority 1/2/3, pin p, archive c — including rebinds)
 *  are left for the board keydown handler. Capture runs in the capture phase
 *  and would otherwise swallow them before priority/pin can fire. */
export function shouldClaimTypeToCapture(e: {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  defaultPrevented: boolean
  target: EventTarget | null
}): boolean {
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return false
  if (e.key.length !== 1) return false
  const t = e.target as HTMLElement | null
  if (t) {
    const tag = t.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false
    if (t.isContentEditable) return false
  }
  // Defer to board hover-gated hotkeys (same registry the cheat-sheet uses).
  const overrides = loadShortcutOverrides()
  const folded = foldUnclaimedUppercaseKey(e.key, overrides)
  const canon = resolveCanonicalKey(folded, overrides)
  if (
    canon !== null
    && KANBAN_SHORTCUTS.some(
      (def) => def.scope === 'hovered-card' && def.matchKey === canon,
    )
  ) {
    return false
  }
  return true
}

export function KanbanCardQuickCapture({
  card,
  api,
  visible,
  currentUserId,
  onSetDueDate,
  onSetActionDate,
  onChanged,
}: KanbanCardQuickCaptureProps) {
  const qc = useQueryClient()
  // Shared card-notes draft key with drawer CommentsSection + peek Thread
  // comments — persist ANY typed string (note / date / action …); classifier
  // still decides the submit path on Enter.
  const { draft, setDraft, clearDraft } = useCommentDraft(currentUserId, card.id)
  const [value, setValue] = useState(draft)
  const draftScope = commentDraftScope(currentUserId, card.id)
  const lastDraftScopeRef = useRef(draftScope)
  if (lastDraftScopeRef.current !== draftScope) {
    lastDraftScopeRef.current = draftScope
    migrateAnonCommentDraft(currentUserId, card.id)
    setValue(readCommentDraft(currentUserId, card.id))
  }
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState<Flash>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setDraftRef = useRef(setDraft)
  setDraftRef.current = setDraft

  // Live read of what ↵ will do, so the hint can preview "due Wed 24 Jun" vs
  // "note" as the operator types — no surprise on Enter.
  const preview = useMemo(() => classifyQuickCapture(value), [value])

  // FEAT-2026-07-02-KANBAN-CARD-TYPE-TO-CAPTURE: while THIS card is hovered,
  // just typing starts the capture (operator: "Hovering over the card I can't
  // type things like action date and such.. or notes directly"). One card is
  // hovered at a time, so at most one listener is live. The first printable
  // key is claimed (preventDefault) and seeded into the controlled value, then
  // focus moves here so the rest of the keystrokes flow natively.
  useEffect(() => {
    if (!visible || typeof document === 'undefined') return
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (!shouldClaimTypeToCapture(e)) return
      const input = inputRef.current
      if (!input || document.activeElement === input) return
      e.preventDefault()
      e.stopPropagation()
      setValue((prev) => {
        const next = prev + e.key
        setDraftRef.current(next)
        return next
      })
      input.focus()
    }
    // Capture phase so board-level single-key shortcuts never race the claim.
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [visible])

  function showFlash(next: Flash) {
    if (flashTimer.current) clearTimeout(flashTimer.current)
    setFlash(next)
    if (next) {
      flashTimer.current = setTimeout(() => setFlash(null), 1700)
    }
  }

  function applyValue(next: string) {
    setValue(next)
    setDraft(next)
  }

  async function submit() {
    const res = classifyQuickCapture(value)
    if (!res || busy) return
    setBusy(true)
    try {
      if (res.kind === 'due') {
        if (onSetDueDate) {
          await onSetDueDate(card, res.iso)
        } else {
          // Fallback for hosts that wired `api` but not the rail's due setter.
          await api.updateCard(card.id, { due_date: res.iso } as never)
        }
        showFlash({ tone: 'ok', text: `Due ${res.label}` })
      } else if (res.kind === 'action') {
        if (onSetActionDate) {
          await onSetActionDate(card, res.iso)
        } else {
          await api.updateCard(card.id, { action_date: res.iso } as never)
        }
        showFlash({ tone: 'ok', text: `Action ${res.label}` })
      } else {
        await api.createComment(card.id, res.body)
        void qc.invalidateQueries({ queryKey: cardCommentsQueryKey(card.id) })
        showFlash({ tone: 'ok', text: 'Note added' })
      }
      setValue('')
      clearDraft()
      onChanged?.(card)
      // Keep focus so the operator can fire off several in a row.
      inputRef.current?.focus()
    } catch {
      showFlash({ tone: 'err', text: "Couldn't save" })
    } finally {
      setBusy(false)
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      void submit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (value) {
        setValue('')
        clearDraft()
      } else inputRef.current?.blur()
    }
  }

  return (
    // FEAT-2026-07-02-KANBAN-CARD-ACTION-CAPSULE: ghost at rest (transparent
    // border, faint fill) so the zone reads as ONE surface with the button rail
    // instead of a third stacked box; the full field chrome (border + white
    // fill) materialises on hover/focus, exactly when the operator engages it.
    <div
      data-card-quick-capture=""
      className="flex items-center gap-1.5 rounded-md border border-transparent bg-neutral-100/50 px-2 py-[3px] text-neutral-600 transition-colors duration-150 hover:border-neutral-200/80 hover:bg-neutral-50 focus-within:border-violet-300 focus-within:bg-white dark:bg-zinc-800/40 dark:text-zinc-300 dark:hover:border-zinc-700/70 dark:hover:bg-zinc-800/60 dark:focus-within:border-violet-500/50 dark:focus-within:bg-zinc-800"
      onClick={swallow}
      onMouseDown={swallow}
      onDoubleClick={swallow}
    >
      <span className="shrink-0 text-neutral-400 dark:text-zinc-500" aria-hidden>
        {preview?.kind === 'due' || preview?.kind === 'action' ? (
          <CalendarClock className="h-3.5 w-3.5" />
        ) : (
          <StickyNote className="h-3.5 w-3.5" />
        )}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={busy}
        // Not a tab stop while the hover zone is collapsed (avoids N tab-stops
        // on a 100-card board); clickable + focus-within keeps it open regardless.
        tabIndex={visible ? 0 : -1}
        onChange={(e) => applyValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Type a note, date, or 'action 22 Jul'…"
        aria-label="Quick add a note, due date, or action date"
        className="min-w-0 flex-1 bg-transparent text-[11.5px] placeholder:text-neutral-400 focus:outline-none disabled:opacity-60 dark:placeholder:text-zinc-500"
      />
      <span className="shrink-0 text-[10px] font-medium" aria-live="polite">
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" aria-label="Saving" />
        ) : flash ? (
          <span
            className={
              flash.tone === 'ok'
                ? 'inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400'
                : 'inline-flex items-center gap-0.5 text-red-600 dark:text-red-400'
            }
          >
            {flash.tone === 'ok' && <Check className="h-3 w-3" aria-hidden />}
            {flash.text}
          </span>
        ) : preview ? (
          <span className="text-neutral-400 dark:text-zinc-500">
            {preview.kind === 'due'
              ? `↵ due ${preview.label}`
              : preview.kind === 'action'
                ? `↵ action ${preview.label}`
                : '↵ note'}
          </span>
        ) : null}
      </span>
    </div>
  )
}
